import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import AdminOverview from './AdminOverview';
import AdminMasterDashboard from './AdminMasterDashboard';
import LeadsManagement from './LeadsManagement';
import CustomersWorkflow from './CustomersWorkflow';
import UserManagement from './UserManagement';
import BranchManagement from './BranchManagement';
import CRMReports from './CRMReports';
import ActivityLogs from './ActivityLogs';
import EmiCalculator from './EmiCalculator';
import DepartmentDashboard from './DepartmentDashboard';
import NotificationsMenu from './NotificationsMenu';
import TaskAssignment from './TaskAssignment';
import AnnouncementBanner from './AnnouncementBanner';
import BackupManagement from '../BackupManagement';
import apiService from '../../../services/apiService';
import './CRMDashboard.css';

const BASE = '/crm/app';

const CRMDashboard: React.FC = () => {
  const [userRole, setUserRole] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [workflowStep, setWorkflowStep] = useState<string | null>(null);
  const [showEmi, setShowEmi] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [branchName, setBranchName] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        // Read from CRM-specific session (prioritize tab-isolated sessionStorage)
        const crmUserStr = sessionStorage.getItem('aswCrmUser') || localStorage.getItem('aswCrmUser');
        const crmToken = sessionStorage.getItem('aswCrmToken') || localStorage.getItem('aswCrmToken');

        if (!crmToken) {
          sessionStorage.removeItem('aswCrmUser');
          sessionStorage.removeItem('aswCrmToken');
          localStorage.removeItem('aswCrmUser');
          localStorage.removeItem('aswCrmToken');
          navigate('/crm');
          return;
        }

        let role = '';
        let name = '';
        let branchId: string | null = null;

        try {
          const verifyRes = await apiService.verifySession(crmToken);
          if (verifyRes.success && verifyRes.user) {
            role = verifyRes.user.role || '';
            name = verifyRes.user.name || '';
            setUserId(verifyRes.user.id || '');
            branchId = verifyRes.user.branch_id || null;
            sessionStorage.setItem('aswCrmUser', JSON.stringify(verifyRes.user));
            localStorage.setItem('aswCrmUser', JSON.stringify(verifyRes.user));
          } else {
            sessionStorage.removeItem('aswCrmUser');
            sessionStorage.removeItem('aswCrmToken');
            localStorage.removeItem('aswCrmUser');
            localStorage.removeItem('aswCrmToken');
            navigate('/crm');
            return;
          }
        } catch (verifyErr) {
          console.error('Session verification error:', verifyErr);
          const errMsg = verifyErr instanceof Error ? verifyErr.message : String(verifyErr);
          if (errMsg.includes('401') || errMsg.includes('403') || errMsg.toLowerCase().includes('expired')) {
            sessionStorage.removeItem('aswCrmUser');
            sessionStorage.removeItem('aswCrmToken');
            localStorage.removeItem('aswCrmUser');
            localStorage.removeItem('aswCrmToken');
            navigate('/crm');
            return;
          }
        }

        if (!role && crmUserStr) {
          const parsed = JSON.parse(crmUserStr);
          role = parsed.role || '';
          name = parsed.name || '';
          setUserId(parsed.id || '');
          branchId = parsed.branch_id || null;
        }

        setUserRole(role);
        setUserName(name);

        if (role && role !== 'Admin') {
          if (branchId) {
            try {
              const branchesRes = await apiService.getCrmBranches();
              if (branchesRes.success && branchesRes.data) {
                const matchedBranch = branchesRes.data.find((b: any) => b.id === branchId);
                if (matchedBranch) {
                  setBranchName(matchedBranch.name);
                }
              }
            } catch (branchErr) {
              console.error('Error fetching branch name:', branchErr);
            }
          }

          if (role !== 'Sales Team') {
            const rolesRes = await apiService.getCrmRoles();
            if (rolesRes.success && rolesRes.data) {
              const matchedRole = rolesRes.data.find((r: any) => r.name === role);
              if (matchedRole && matchedRole.workflow_step) {
                setWorkflowStep(matchedRole.workflow_step);
              }
            }
          }
        }

        setRolesLoaded(true);
      } catch (e) {
        console.error('CRM init error:', e);
        setRolesLoaded(true);
      }
    };
    init();
  }, []);

  // Active Presence Heartbeat: periodically sends ping to keep user 'Available' in DB while CRM is open
  useEffect(() => {
    if (!userId || userId === 'legacy-admin' || userId === 't-permit-admin') return;

    // Send initial ping immediately
    apiService.pingActivity(userId).catch(() => {});

    // Periodic heartbeat every 45 seconds (engine marks offline after 5 mins)
    const interval = setInterval(() => {
      apiService.pingActivity(userId).catch(() => {});
    }, 45 * 1000);

    // Throttled user activity listener (at most once every 25 seconds)
    let lastPingTime = Date.now();
    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastPingTime > 25 * 1000) {
        lastPingTime = now;
        apiService.pingActivity(userId).catch(() => {});
      }
    };

    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('click', handleUserActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
    };
  }, [userId]);

  const handleCrmLogout = () => {
    if (userId) {
      apiService.logoutCrm(userId).catch(() => {});
    }
    sessionStorage.removeItem('aswCrmUser');
    sessionStorage.removeItem('aswCrmToken');
    localStorage.removeItem('aswCrmUser');
    localStorage.removeItem('aswCrmToken');
    navigate('/crm');
  };

  // Inactivity Auto-logout (15 minutes)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        alert('Your session has expired due to inactivity. You have been logged out.');
        handleCrmLogout();
      }, 15 * 60 * 1000); // 15 minutes
    };

    // Events to monitor for activity
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('mousedown', resetTimer);
    window.addEventListener('scroll', resetTimer);
    window.addEventListener('touchstart', resetTimer);

    resetTimer(); // Start timer on mount

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('mousedown', resetTimer);
      window.removeEventListener('scroll', resetTimer);
      window.removeEventListener('touchstart', resetTimer);
    };
  }, [userId, userRole]);

  const isActive = (path: string) => location.pathname === path ? 'active-crm-tab' : '';
  const isAdmin = userRole === 'Admin';
  const isManager = userRole === 'Branch Manager';
  const isSales = userRole === 'Sales Team';
  const isDepartment = rolesLoaded && !isAdmin && !isManager && !isSales && !!workflowStep;

  const buildNavLinks = () => {
    if (isAdmin || isManager) {
      const links = [
        { label: 'Dashboard',  to: `${BASE}/` },
        { label: 'Overview',   to: `${BASE}/overview` },
        { label: 'Leads',      to: `${BASE}/leads` },
        { label: 'Customers',  to: `${BASE}/customers` },
        { label: 'Tasks',      to: `${BASE}/tasks` },
      ];
      if (isAdmin) {
        links.push({ label: 'Branches', to: `${BASE}/branches` });
      }
      links.push(
        { label: 'Users',      to: `${BASE}/users` },
        { label: 'Reports',    to: `${BASE}/reports` },
        { label: 'Logs',       to: `${BASE}/logs` }
      );
      if (isAdmin) {
        links.push({ label: '🛡️ Backups', to: `${BASE}/backups` });
      }
      return links;
    }
    if (isSales) {
      return [
        { label: '🎯 My Leads',  to: `${BASE}/` },
      ];
    }
    return [
      { label: `⚙ ${workflowStep || 'My Tasks'}`, to: `${BASE}/` },
      { label: '📋 Tasks', to: `${BASE}/tasks` },
    ];
  };

  const navLinks = buildNavLinks();

  if (!rolesLoaded) {
    return <div className="crm-loading"><div className="crm-spinner"></div> Loading CRM...</div>;
  }

  const getDepartmentThemeClass = () => {
    if (isAdmin || isManager) return 'crm-theme-admin';
    if (isSales) return 'crm-theme-sales';
    if (isDepartment && workflowStep) {
      const stepClass = workflowStep.toLowerCase().replace(/\s+/g, '-');
      if (stepClass.includes('docs') || stepClass.includes('document')) return 'crm-theme-docs';
      if (stepClass.includes('loan')) return 'crm-theme-loan';
      if (stepClass.includes('verify') || stepClass.includes('verification')) return 'crm-theme-verification';
      if (stepClass.includes('account') || stepClass.includes('agreement')) return 'crm-theme-account';
      if (stepClass.includes('disburse') || stepClass.includes('payment')) return 'crm-theme-disbursed';
      if (stepClass.includes('deliver') || stepClass.includes('rto')) return 'crm-theme-delivered';
      return `crm-theme-${stepClass}`;
    }
    return '';
  };

  const themeClass = getDepartmentThemeClass();

  return (
    <div className={`crm-dashboard-container ${themeClass}`}>
      {showEmi && <EmiCalculator onClose={() => setShowEmi(false)} />}

      <div className="crm-top-bar">
        <div className="crm-top-bar-title">
          <div className="crm-icon-badge" style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5 }}>CRM</div>
          <div>
            <h1 style={{ margin: 0 }}>CRM {isDepartment ? `· ${workflowStep}` : ''}</h1>
            {userName && (
              <div style={{ fontSize: '11.5px', color: 'var(--crm-text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                {isAdmin ? (
                  <span style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6', padding: '2px 8px', borderRadius: 12, fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                    👑 Master Admin
                  </span>
                ) : isManager ? (
                  <>
                    <span style={{ fontWeight: 500, color: 'var(--crm-text-main)' }}>{userName}</span>
                    <span style={{ color: 'var(--crm-text-muted)' }}>·</span>
                    <span style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#d97706', padding: '2px 8px', borderRadius: 12, fontWeight: 700, fontSize: '11px' }}>
                      📍 {branchName || 'Branch'} Manager
                    </span>
                  </>
                ) : (
                  <>
                    <span style={{ fontWeight: 500, color: 'var(--crm-text-main)' }}>{userName}</span>
                    <span style={{ color: 'var(--crm-text-muted)' }}>·</span>
                    {branchName ? (
                      <span style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#059669', padding: '2px 8px', borderRadius: 12, fontWeight: 700, fontSize: '10.5px' }}>
                        🏢 {branchName} ({userRole})
                      </span>
                    ) : (
                      <span style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#059669', padding: '2px 8px', borderRadius: 12, fontWeight: 700, fontSize: '10.5px' }}>
                        🏢 Main Branch/Thane ({userRole})
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <nav className="crm-navigation">
          {navLinks.map(n => (
            <Link key={n.to} to={n.to} className={`crm-nav-link ${isActive(n.to)}`}>
              {n.label}
            </Link>
          ))}
          <button className="crm-nav-link" onClick={() => setShowEmi(true)} style={{ border: 'none', cursor: 'pointer', background: 'transparent' }}>
            EMI Calc
          </button>
          
          {userId && <NotificationsMenu userId={userId} />}

          <button
            className="crm-nav-link"
            onClick={handleCrmLogout}
            style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--crm-danger)' }}
            title="Exit CRM"
          >
            Exit
          </button>
        </nav>
      </div>

      <div className="crm-content-area">
        <AnnouncementBanner isAdmin={isAdmin} userId={userId} />
        <Routes>
          {(isAdmin || isManager) && (
            <>
              <Route path="/" element={<AdminMasterDashboard />} />
              <Route path="/overview" element={<AdminOverview />} />
              <Route path="/leads" element={<LeadsManagement userRole={userRole} />} />
              <Route path="/customers" element={<CustomersWorkflow userRole={userRole} />} />
              <Route path="/tasks" element={<TaskAssignment isAdmin={true} userId={userId} userRole={userRole} />} />
              {isAdmin && <Route path="/branches" element={<BranchManagement />} />}
              <Route path="/users" element={<UserManagement />} />
              <Route path="/reports" element={<CRMReports />} />
              <Route path="/logs" element={<ActivityLogs />} />
              {isAdmin && <Route path="/backups" element={<BackupManagement />} />}
            </>
          )}
          {isSales && (
            <>
              <Route path="/" element={<LeadsManagement userRole={userRole} />} />
              <Route path="/customers" element={<CustomersWorkflow userRole={userRole} />} />
            </>
          )}
          {isDepartment && (
            <>
              <Route path="/" element={<DepartmentDashboard userRole={userRole} workflowStep={workflowStep!} />} />
              <Route path="/tasks" element={<TaskAssignment isAdmin={false} userId={userId} userRole={userRole} />} />
            </>
          )}
          {!isAdmin && !isManager && !isSales && !isDepartment && rolesLoaded && (
            <Route path="/" element={
              <div className="crm-empty-state">
                <div className="crm-empty-state-icon">🔒</div>
                <h3>No dashboard assigned</h3>
                <p>Your role (<strong>{userRole || 'Unknown'}</strong>) hasn't been mapped to a workflow step. Ask the Admin.</p>
              </div>
            } />
          )}
        </Routes>
      </div>
    </div>
  );
};

export default CRMDashboard;
